/**
 * The questions and answers at the end of a blog article, read from its HTML,
 * for a FAQPage node next to the BlogPosting. An article has a FAQ when it has
 * an <h2> that says "Frequently asked questions" (or "FAQ") followed by
 * <h3>question</h3><p>answer</p> pairs; the list ends at the next <h2>.
 *
 * Pure TS, shared by the React page and the Netlify edge layer (Deno): no aliases, no DOM.
 */
const strip = (html: string) =>
  html
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&#39;|&apos;/g, "'")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/\s+/g, " ")
    .trim();

export function extractArticleFaqs(html: string | null | undefined): { q: string; a: string }[] {
  if (!html) return [];
  const head = /<h2[^>]*>\s*(?:frequently asked questions|faqs?)\b[^<]*<\/h2>/i.exec(html);
  if (!head) return [];
  const rest = html.slice(head.index + head[0].length);
  const end = rest.search(/<h2[\s>]/i);
  const block = end < 0 ? rest : rest.slice(0, end);
  const out: { q: string; a: string }[] = [];
  const re = /<h3[^>]*>([\s\S]*?)<\/h3>([\s\S]*?)(?=<h3[\s>]|$)/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(block))) {
    const q = strip(m[1]);
    const a = strip(m[2]);
    if (q && a) out.push({ q, a });
  }
  return out;
}

/** The FAQPage node for an article's @graph, or null when it has no FAQ. */
export function articleFaqNode(canonical: string, html: string | null | undefined): Record<string, unknown> | null {
  const faqs = extractArticleFaqs(html);
  if (!faqs.length) return null;
  return {
    "@type": "FAQPage",
    "@id": `${canonical}#faq`,
    mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  };
}
